import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import { addMinutes, format, isSameDay, parse, parseISO } from 'date-fns'
import { CheckoutPickerField } from './fields'
import { NumberStepper } from './components'
import { validateStartTime } from './schema'
import type { CheckoutForm } from './useCheckoutForm'

const formatWage = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

type Props = {
  form: CheckoutForm
  idx: number
  skillName: string
  wage?: number
  minDuration: number
  maxDuration: number
  locked: boolean
}

export const PositionCard: React.FC<Props> = ({
  form,
  idx,
  skillName,
  wage,
  minDuration,
  maxDuration,
  locked,
}) => (
  <Box
    sx={{
      backgroundColor: 'grey.200',
      border: (t) => `1px solid ${t.palette.neutral.lightGray}`,
      borderRadius: '3px',
      padding: '10px 12px',
      // white inner fields on the grey card (mirrors the Stripe widget)
      '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
        backgroundColor: 'common.white',
      },
    }}
  >
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: '8px',
      }}
    >
      <Typography
        sx={{
          fontFamily: (theme) => theme.app.monoFontFamily,
          fontSize: 12,
          color: 'common.black',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {skillName}
      </Typography>
      {wage != null && (
        <Typography
          sx={{
            fontFamily: (theme) => theme.app.monoFontFamily,
            fontSize: 12,
            color: 'neutral.gray',
          }}
        >
          {formatWage(wage)}/hr
        </Typography>
      )}
    </Stack>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '12px',
        rowGap: 0,
      }}
    >
      <form.Subscribe selector={(s) => s.values.date}>
        {(dateStr) => {
          const selected = dateStr ? parseISO(dateStr) : null
          const minTime =
            selected && isSameDay(selected, new Date())
              ? addMinutes(new Date(), 15)
              : undefined
          return (
            <form.Field
              name={`positions[${idx}].startTime`}
              validators={{
                onChange: ({ value }) => validateStartTime(value, dateStr),
              }}
            >
              {(field) => {
                const value = field.state.value as string
                const dateValue = value
                  ? parse(value, 'HH:mm', new Date())
                  : null
                // Hold the error until the field is touched or a submit attempt
                // invalidates it — otherwise the empty default flags on mount.
                const showError =
                  field.state.meta.isTouched || !field.state.meta.isValid
                const helperText = showError
                  ? validateStartTime(value, dateStr)
                  : undefined
                const hasError = !!helperText
                const helperId = `start-time-${idx}-helper`
                return (
                  <>
                    <TimeField
                      label="Start Time"
                      format="hh:mm a"
                      disabled={locked}
                      minTime={minTime}
                      value={dateValue}
                      onChange={(v) =>
                        field.handleChange(v ? format(v, 'HH:mm') : '')
                      }
                      slots={{ textField: CheckoutPickerField }}
                      slotProps={{
                        textField: {
                          id: `start-time-${idx}`,
                          onBlur: field.handleBlur,
                          error: hasError,

                          slotProps: {
                            formHelperText: { sx: { display: 'none' } },

                            htmlInput: {
                              'aria-describedby':
                                hasError && helperText ? helperId : undefined,
                            },
                          },
                        },
                      }}
                    />
                    <form.Field name={`positions[${idx}].duration`}>
                      {(durField) => (
                        <NumberStepper
                          id={`duration-${idx}`}
                          label="Duration"
                          value={durField.state.value as number}
                          min={minDuration}
                          max={maxDuration}
                          disabled={locked}
                          onChange={(v) => durField.handleChange(v)}
                          suffix="h"
                        />
                      )}
                    </form.Field>
                    {hasError && helperText && (
                      <Typography
                        id={helperId}
                        role="alert"
                        sx={{
                          gridColumn: '1 / -1',
                          mt: '4px',
                          fontFamily: (theme) => theme.app.monoFontFamily,
                          fontSize: 11,
                          color: 'error.main',
                        }}
                      >
                        {helperText}
                      </Typography>
                    )}
                  </>
                )
              }}
            </form.Field>
          )
        }}
      </form.Subscribe>
    </Box>
  </Box>
)
